/**
 * Canonical role labels + the `CardRole` view-layer enum for the GvG planner.
 *
 * `CardRole` is a **view-layer** role that differs from the persisted
 * `PlayerRole` (TANK / DPS / HYBRID / HEAL) in one way:
 *
 *   - It adds a `Nameless` state: `PlayerRole` does not have a `Nameless`
 *     state because every weapon is tagged with one of the three combat
 *     roles. A freshly-added player with no weapons equipped is
 *     `CardRole.Nameless` here — a card state, not a weapon state.
 * Keep `CardRole` distinct from `PlayerRole`; the persisted role comes from
 * the server's exact loadout rule, while `Nameless` is only an empty-card
 * presentation state.
 *
 * The two label maps used to live inline in `RoleBadge.vue` and
 * `WeaponPicker.vue` and drifted: `WeaponPicker` used mixed-case
 * `Tank/DPS/Heal`, `RoleBadge` used uppercase `HEAL/TANK/…`. Both files
 * now import from this single source.
 *
 * The presentation-only `weaponRoleLabel` / `weaponDisplayName` factories read from a per-domain locale catalog so
 * the same weapon slug always renders the right localized name and role
 * label across every planner surface (picker, stats popup, builds popup,
 * `/my-builds` rows, etc.). They never reach into the persisted weapon
 * catalog's `name` field except as a fallback when the catalog has no
 * entry for the slug — that fallback preserves user-authored aliases.
 */

import { WeaponRole } from '@/types/weapon'
import type { PlayerRole } from '@/types/weapon'

export const CardRole = {
  Heal: 'heal',
  Tank: 'tank',
  Dps: 'dps',
  Nameless: 'nameless',
  Hybrid: 'hybrid',
} as const

export type CardRole = (typeof CardRole)[keyof typeof CardRole]

/**
 * Shape every per-domain locale catalog's `roles` block must satisfy.
 * Components hand their unwrapped `messages.value.roles` (or
 * `messages.roles`) to the factories below — keeping the shape in one
 * place stops components from re-deriving the map per file.
 */
export interface RoleDisplayMessages {
  readonly weapon: Readonly<Record<WeaponRole, string>>
  readonly player: Readonly<Record<PlayerRole, string>>
}

/** Presentation-only: resolve a weapon-role bucket label from the catalog. */
export function weaponRoleLabel(role: WeaponRole, messages: RoleDisplayMessages): string {
  return messages.weapon[role]
}

/**
 * Presentation-only: resolve a localized weapon display name from the
 * per-domain catalog, falling back to the persisted `name` from the
 * stable weapon catalog so user-authored aliases survive missing catalog
 * entries. The persisted weapon catalog owns image paths and role colors;
 * the locale catalog owns text only.
 */
export function weaponDisplayName(
  slug: string,
  persistedName: string,
  localizedNames: Readonly<Record<string, string>>,
): string {
  return localizedNames[slug] ?? persistedName
}
